import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Login from './pages/Login';

// ── Global 401 interceptor — any expired session redirects to login ────────
axios.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      const path = window.location.hash;
      localStorage.removeItem('adminToken');
      localStorage.removeItem('currentUser');
      // Reload to trigger login screen, preserve current page in hash
      window.location.reload();
    }
    return Promise.reject(err);
  }
);
import AgentConnections from './pages/AgentConnections';
import Devices from './pages/Devices';
import Patches from './pages/Patches';
import Compliance from './pages/Compliance';
import ValidateUrls from './pages/ValidateUrls';
import PatchVulnCompliance from './pages/PatchVulnCompliance';
import Diagnostics from './pages/Diagnostics';
import PatchDeployment from './pages/PatchDeployment';
import PatchReleases from './pages/PatchReleases';
import PatchingSchedule from './pages/PatchingSchedule';
import UserManagement from './pages/UserManagement';
import EmergencyPatches from './pages/EmergencyPatches';
import RollbackMechanism from './pages/RollbackMechanism';
import RiskMatrix from './pages/RiskMatrix';
import AssetDiscovery from './pages/AssetDiscovery';
import NotificationBell from './components/NotificationBell';
import UserProfile from './components/UserProfile';
import ApprovalMatrix from './pages/ApprovalMatrix';
import AuditLog from './pages/AuditLog';
import EolMonitor from './pages/EolMonitor';
import NotificationSettings from './pages/NotificationSettings';
import AiPriority from './pages/AiPriority';
import AiChat from './pages/AiChat';
import SoftwareLibrary from './pages/SoftwareLibrary';
import BitLockerStatus from './pages/BitLockerStatus';
import CertificateManagement from './pages/CertificateManagement';
// New SureMDM-style pages
import Enrollment    from './pages/Enrollment';
import RemoteActions from './pages/RemoteActions';
import FileStore     from './pages/FileStore';
import DeviceProfiles from './pages/DeviceProfiles';
import Jobs          from './pages/Jobs';
import Reports       from './pages/Reports';
// Phase 1 missing pages
import Webhooks          from './pages/Webhooks';
import ConditionalAccess from './pages/ConditionalAccess';
import DeviceGroups      from './pages/DeviceGroups';
import ApiKeys           from './pages/ApiKeys';
import Scripting         from './pages/Scripting';
// Phase 2 pages
import SSO           from './pages/SSO';
import Organizations from './pages/Organizations';
import Geofencing    from './pages/Geofencing';
import MobileMDM     from './pages/MobileMDM';
// Phase 3 pages
import PushNotifications from './pages/PushNotifications';
import ScimConfig        from './pages/ScimConfig';

// ── Nav groups — Settings group only shown to admin ────────────────────────
function buildNavGroups(role) {
  const groups = [
    {
      label: 'Overview',
      items: [
        { id: 'diagnostics', label: 'Executive Dashboard', icon: '📊' },
        { id: 'connections', label: 'Endpoint Status', icon: '◉' },
      ]
    },
    {
      label: 'Patch Management',
      items: [
        { id: 'releases',   label: 'Patch Releases',   icon: '⬆' },
        { id: 'ai-priority',label: 'AI Priority',       icon: '🧠' },
        { id: 'ai-chat',    label: 'AI Assistant',      icon: '🤖' },
        { id: 'schedule',   label: 'Patch Schedule',   icon: '◷' },
        { id: 'deployment', label: 'Patch Deployment', icon: '⊞' },
        { id: 'patches',    label: 'Patch Catalog',    icon: '⊟' },
        { id: 'emergency',  label: 'Emergency Patches', icon: '🚨' },
        { id: 'rollback',   label: 'Rollback',          icon: '↩' },
      ]
    },
    {
      label: 'Compliance',
      items: [
        { id: 'pvc',        label: 'P&V Compliance',      icon: '⛨' },
        { id: 'compliance', label: 'Patch Status',        icon: '≡' },
        { id: 'risk',       label: 'Risk Matrix',         icon: '⚠' },
        { id: 'bitlocker',  label: 'BitLocker Encryption', icon: '🔐' },
      ]
    },
    {
      label: 'Infrastructure',
      items: [
        { id: 'assets',          label: 'Asset Discovery',   icon: '🖥' },
        { id: 'eol',             label: 'EOL Monitor',        icon: '📅' },
        { id: 'software-library',label: 'Software Library',   icon: '📦' },
        { id: 'devices',         label: 'Devices',           icon: '⊡' },
        { id: 'validate',        label: 'Validate URLs',     icon: '⊕' },
      ]
    },
    {
      label: 'MDM',
      items: [
        { id: 'enrollment',      label: 'Enrollment / QR',    icon: '📱' },
        { id: 'remote-actions',  label: 'Remote Actions',     icon: '⚡' },
        { id: 'file-store',      label: 'File Store',         icon: '📁' },
        { id: 'profiles',        label: 'Device Profiles',    icon: '⚙️'  },
        { id: 'device-groups',   label: 'Device Groups',      icon: '👥' },
        { id: 'jobs',            label: 'Jobs',               icon: '⏰' },
        { id: 'reports',         label: 'Reports',            icon: '📊' },
        { id: 'scripting',       label: 'Scripting',          icon: '📜' },
        { id: 'geofencing',      label: 'Geofencing',         icon: '📍' },
        { id: 'mobile-mdm',      label: 'Mobile / MDM',       icon: '📲' },
      ]
    },
    {
      label: 'Security',
      items: [
        { id: 'webhooks',        label: 'Webhooks',           icon: '🔔' },
        { id: 'conditional-access', label: 'Conditional Access', icon: '🛡' },
        { id: 'api-keys',        label: 'API Keys',           icon: '🔑' },
        { id: 'sso',             label: 'SSO',                icon: '🔐' },
        { id: 'push',            label: 'Push Notifications', icon: '📤' },
        { id: 'scim',            label: 'SCIM Provisioning',  icon: '🔄' },
        { id: 'orgs',            label: 'Organizations',      icon: '🏢' },
      ]
    },
  ];

  // Settings group — admin only
  if (role === 'admin') {
    groups.push({
      label: 'Settings',
      items: [
        { id: 'approvals', label: 'Approval Matrix',      icon: '✅' },
        { id: 'audit',     label: 'Audit Repository',     icon: '📋' },
        { id: 'notifications', label: 'Notifications',    icon: '📧' },
        { id: 'users',     label: 'User Management',      icon: '👤' },
        { id: 'certs',     label: 'Certificates & CRL',   icon: '🔑' },
      ]
    });
  }

  return groups;
}

const PAGE_META = {
  diagnostics: { title: 'Executive Dashboard', subtitle: 'Security posture, patch health and risk overview' },
  connections:  { title: 'Endpoint Status',  subtitle: 'Live endpoint connection monitoring' },
  releases:     { title: 'Patch Releases',   subtitle: 'Available patches and release notes' },
  'ai-priority':{ title: 'AI Priority',      subtitle: 'Patches ranked by AI score · Severity + Exploit risk + Age + Devices affected' },
  'ai-chat':    { title: 'AI Assistant',     subtitle: 'Ask questions about your endpoints, patches, and compliance · Powered by Gemini' },
  schedule:     { title: 'Patch Schedule',   subtitle: 'Scheduled patch deployment windows' },
  deployment:   { title: 'Patch Deployment', subtitle: 'Active and historical deployments' },
  patches:      { title: 'Patch Catalog',    subtitle: 'All patches in the system catalog' },
  pvc:          { title: 'P&V Compliance',   subtitle: 'Patch and vulnerability compliance status' },
  compliance:   { title: 'Patch Status',     subtitle: 'Per-device patch deployment status' },
  devices:      { title: 'Devices',          subtitle: 'Registered endpoints and hardware info' },
  validate:     { title: 'Validate URLs',    subtitle: 'Test patch download URLs' },
  assets:       { title: 'Asset Discovery',    subtitle: 'Hardware inventory and software catalog per endpoint' },
  eol:          { title: 'EOL Monitor',        subtitle: 'End-of-life tracking for OS and applications · 90-day advance alerts' },
  'software-library': { title: 'Software Library', subtitle: 'Admin-curated apps · Request installs · Silent deploy via agent · No UAC' },
  risk:         { title: 'Risk Matrix',        subtitle: 'Risk scoring and prioritization linked to vulnerability management' },
  bitlocker:    { title: 'BitLocker Encryption', subtitle: 'Drive encryption status · Recovery key vault · AES-256 encrypted keys · Admin-enforced' },
  emergency:    { title: 'Emergency Patches',  subtitle: 'Push critical patches immediately to endpoints' },
  rollback:     { title: 'Rollback',           subtitle: 'Revert deployed patches on specific endpoints' },
  users:        { title: 'User Management',    subtitle: 'Manage dashboard users and roles' },
  approvals:    { title: 'Approval Matrix',    subtitle: 'Role-based approval workflow for high-impact patch actions' },
  audit:        { title: 'Audit Repository',   subtitle: 'Immutable records — 7-year retention for compliance' },
  notifications:{ title: 'Notifications',      subtitle: 'Email reports, SMTP configuration and security hardening status' },
  certs:        { title: 'Certificates & CRL',  subtitle: 'Self-signed CA · TLS certificate status · CRL revocation control · Distribution point' },
  // New MDM pages
  enrollment:          { title: 'Device Enrollment',      subtitle: 'QR codes and one-click install links for easy agent deployment' },
  'remote-actions':    { title: 'Remote Actions',          subtitle: 'Reboot, shutdown, lock screen, run commands on endpoints instantly' },
  'file-store':        { title: 'File Store',              subtitle: 'Upload scripts, installers and config files — push to endpoints on demand' },
  profiles:            { title: 'Device Profiles',         subtitle: 'Policy templates — Windows Update, BitLocker, firewall, passwords applied to endpoints' },
  'device-groups':     { title: 'Device Groups',           subtitle: 'Static and dynamic groups, device tags, bulk CSV enrollment' },
  jobs:                { title: 'Jobs',                     subtitle: 'Recurring task templates — scan, patch, profile, collect, command on a schedule' },
  reports:             { title: 'Reports',                  subtitle: 'On-demand reports — compliance, patch status, inventory, EOL, security · JSON & CSV' },
  scripting:           { title: 'Scripting Engine',         subtitle: 'Run PowerShell, Bash, Python scripts on managed endpoints — library of built-in templates' },
  geofencing:          { title: 'Geofencing',               subtitle: 'Location-based policies — trigger actions when devices enter or leave zones' },
  'mobile-mdm':        { title: 'Mobile & Multi-Platform', subtitle: 'iOS, Android, macOS enrollment — MDM profiles, mobileconfig, zero-touch' },
  webhooks:            { title: 'Webhooks',                 subtitle: 'Real-time outbound alerts to Slack, Teams, SIEM, email and custom endpoints' },
  'conditional-access':{ title: 'Conditional Access',       subtitle: 'Block or warn devices based on compliance score, BitLocker, AV status' },
  'api-keys':          { title: 'API Keys',                 subtitle: 'Scoped API tokens for SIEM integrations, custom scripts and third-party tools' },
  sso:                 { title: 'Single Sign-On',           subtitle: 'SAML 2.0 and OIDC SSO — Okta, Azure AD, Google Workspace, custom IdP' },
  push:                { title: 'Push Notifications',       subtitle: 'APNs, FCM, WNS push — wake agents instantly instead of waiting for poll interval' },
  scim:                { title: 'SCIM 2.0 Provisioning',    subtitle: 'Auto-sync users and groups from Okta, Azure AD, Google Workspace · RFC 7643/7644' },
  orgs:                { title: 'Organizations',            subtitle: 'Multi-tenant management — isolate device fleets per organization (MSP mode)' },
};

// ── URL ↔ page ID mapping ─────────────────────────────────────────────────
const PAGE_SLUGS = {
  'executive-dashboard': 'diagnostics',
  'endpoint-status':     'connections',
  'patch-releases':      'releases',
  'ai-priority':         'ai-priority',
  'ai-chat':             'ai-chat',
  'patch-schedule':      'schedule',
  'patch-deployment':    'deployment',
  'patch-catalog':       'patches',
  'emergency-patches':   'emergency',
  'rollback':            'rollback',
  'pv-compliance':       'pvc',
  'patch-status':        'compliance',
  'risk-matrix':         'risk',
  'bitlocker-encryption':'bitlocker',
  'asset-discovery':     'assets',
  'eol-monitor':         'eol',
  'software-library':    'software-library',
  'devices':             'devices',
  'validate-urls':       'validate',
  'enrollment':          'enrollment',
  'remote-actions':      'remote-actions',
  'file-store':          'file-store',
  'device-profiles':     'profiles',
  'device-groups':       'device-groups',
  'jobs':                'jobs',
  'reports':             'reports',
  'scripting':           'scripting',
  'geofencing':          'geofencing',
  'mobile-mdm':          'mobile-mdm',
  'webhooks':            'webhooks',
  'conditional-access':  'conditional-access',
  'api-keys':            'api-keys',
  'sso':                 'sso',
  'push-notifications':  'push',
  'scim-provisioning':   'scim',
  'organizations':       'orgs',
  'remote-actions':      'remote-actions',
  'file-store':          'file-store',
  'device-profiles':     'profiles',
  'jobs':                'jobs',
  'reports':             'reports',
  'approval-matrix':     'approvals',
  'audit-repository':    'audit',
  'notifications':       'notifications',
  'user-management':     'users',
  'certificates-crl':   'certs',
};
// Reverse map: page ID → slug
const PAGE_ID_TO_SLUG = Object.fromEntries(
  Object.entries(PAGE_SLUGS).map(([slug, id]) => [id, slug])
);

function getPageFromHash() {
  // Support both #/slug and #slug formats
  const raw  = window.location.hash.replace(/^#\/?/, '').toLowerCase().trim();
  return PAGE_SLUGS[raw] || 'diagnostics';
}

function setHashForPage(pageId) {
  const slug    = PAGE_ID_TO_SLUG[pageId] || pageId;
  const newHash = '/' + slug;
  // Use location.hash directly — triggers hashchange correctly
  if (window.location.hash !== '#' + newHash) {
    window.location.hash = newHash;
  }
  // Update browser tab title
  const meta = PAGE_META[pageId];
  if (meta) document.title = meta.title + ' — PatchSphere AI';
}

// Role badge colours
const ROLE_STYLE = {
  admin:  { background: '#eff4ff', color: '#1a56db', border: '1px solid #c7d7fd' },
  viewer: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
};

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const [user,  setUser]  = useState(() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; }
  });
  // Initialise page from URL hash so direct links and refresh work
  const [page, setPage] = useState(() => getPageFromHash());
  const [themeMode,  setThemeMode]  = useState(() => localStorage.getItem('themeMode') || 'light');
  const [searchVal,  setSearchVal]  = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  // Keep hash in sync when page changes programmatically (sidebar click, card click, etc.)
  function navigate(pageId) {
    setPage(pageId);
    setHashForPage(pageId);
  }

  // Handle browser back/forward buttons via hashchange
  useEffect(() => {
    function onHashChange() {
      const newPage = getPageFromHash();
      setPage(newPage);
      // Update title on back/forward too
      const m = PAGE_META[newPage];
      if (m) document.title = m.title + ' — PatchSphere AI';
    }
    window.addEventListener('hashchange', onHashChange);
    // Set hash + title for the initial page on mount
    setHashForPage(page);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []); // eslint-disable-line

  // Apply theme — use class on body for reliable CSS cascade
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');

    function applyTheme(mode) {
      const resolved = mode === 'system' ? (mq.matches ? 'dark' : 'light') : mode;
      document.body.classList.remove('theme-light', 'theme-dark');
      document.body.classList.add('theme-' + resolved);
      // Also set data-theme for components that use it
      document.documentElement.setAttribute('data-theme', resolved);
    }

    applyTheme(themeMode);
    localStorage.setItem('themeMode', themeMode);

    // Listen for OS preference change when in system mode
    const handler = () => { if (themeMode === 'system') applyTheme('system'); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themeMode]);

  const THEME_ICONS  = { light: '☀', dark: '🌙', system: '⊙' };
  const THEME_LABELS = { light: 'Light', dark: 'Dark', system: 'System' };
  const THEME_CYCLE  = { light: 'dark', dark: 'system', system: 'light' };

  function cycleTheme() { setThemeMode(t => THEME_CYCLE[t] || 'light'); }

  // Search — navigate to relevant page based on query
  function handleSearch(e) {
    e.preventDefault();
    const q = searchVal.trim().toLowerCase();
    if (!q) return;
    if (/^lp\d|^pc|hostname|device/i.test(q)) { navigate('assets'); }
    else if (/patch|update|browser|chrome|firefox|edge|dell|hp/i.test(q)) { navigate('patches'); }
    else if (/vuln|cve|risk/i.test(q)) { navigate('risk'); }
    else if (/compliance|score/i.test(q)) { navigate('pvc'); }
    else { navigate('assets'); }
    setSearchOpen(false);
  }

  useEffect(() => {
    if (token) localStorage.setItem('adminToken', token);
  }, [token]);

  // ── Login handler ──────────────────────────────────────────────────────
  function handleLogin(newToken, newUser) {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('adminToken', newToken);
    localStorage.setItem('currentUser', JSON.stringify(newUser));
  }

  // ── Logout ─────────────────────────────────────────────────────────────
  function logout() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('currentUser');
    setToken('');
    setUser(null);
  }

  if (!token) return <Login onLogin={handleLogin} />;

  const role       = user?.role || 'viewer';
  const username   = user?.username || 'Admin';
  const navGroups  = buildNavGroups(role);
  const meta       = PAGE_META[page] || { title: page, subtitle: '' };
  const currentGroup = navGroups.find(g => g.items.some(i => i.id === page));

  // Avatar initials
  const initials = username.slice(0, 2).toUpperCase();

  return (
    <div className="layout">

      {/* ── Sidebar ── */}
      <aside className="sidebar">
        <div style={{ borderBottom: '1px solid #e8ecf4', background: '#fff', textAlign: 'center', lineHeight: 0 }}>
          <img
            src="/patchsphere-logo.png"
            alt="PatchSphere AI"
            style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain', marginBottom: -8 }}
          />
          <svg viewBox="0 0 220 44" style={{ width: '75%', maxWidth: 150, height: 'auto', display: 'block', margin: '0 auto', marginBottom: 6 }} xmlns="http://www.w3.org/2000/svg">
            <text x="0" y="30" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900" fontSize="32" fill="#1e2d6b" letterSpacing="-0.5">Covasant</text>
            <text x="2" y="42" fontFamily="Arial, sans-serif" fontWeight="400" fontSize="10" fill="#94a3b8" letterSpacing="0.2">AI-Driven. Human-Inspired.</text>
          </svg>
        </div>

        <nav className="sidebar-nav">
          {navGroups.map(group => (
            <div key={group.label}>
              <div className="nav-section-label">{group.label}</div>
              {group.items.map(item => (
                <div
                  key={item.id}
                  className={`nav-item ${page === item.id ? 'active' : ''}`}
                  onClick={() => navigate(item.id)}
                  title={`/#/${PAGE_ID_TO_SLUG[item.id] || item.id}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{username}</div>
              {/* Role badge */}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 7px',
                borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.4px',
                ...(ROLE_STYLE[role] || ROLE_STYLE.viewer)
              }}>
                {role}
              </span>
            </div>
          </div>
          <button
            className="btn btn-ghost"
            style={{ width: '100%', justifyContent: 'center', fontSize: 12 }}
            onClick={logout}
          >
            ⎋ &nbsp;Log out
          </button>
          <div className="version-tag" style={{ textAlign: 'center', marginTop: 8 }}>v1.0.0</div>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="main">
        <div className="topbar">
          <div className="topbar-left">
            <div className="topbar-title">{meta.title}</div>
            <div className="topbar-breadcrumb">
              <span>PatchSphere AI</span>
              <span style={{ color: '#cbd5e1' }}>›</span>
              {currentGroup && (
                <><span>{currentGroup.label}</span><span style={{ color: '#cbd5e1' }}>›</span></>
              )}
              <span style={{ color: '#1a56db', fontWeight: 500 }}>{meta.title}</span>
              {/* Clickable URL chip */}
              <span
                title="Click to copy URL"
                onClick={() => {
                  const url = window.location.origin + window.location.pathname + '#/' + (PAGE_ID_TO_SLUG[page] || page);
                  navigator.clipboard?.writeText(url).catch(()=>{});
                }}
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  color: '#94a3b8',
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: 5,
                  padding: '1px 7px',
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  letterSpacing: '0.2px',
                  userSelect: 'all',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.target.style.background = '#e8f0fe'}
                onMouseLeave={e => e.target.style.background = '#f1f5f9'}
              >
                /#/{PAGE_ID_TO_SLUG[page] || page}
              </span>
            </div>
          </div>

          <div className="topbar-right">
            {/* Functional search */}
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                background: '#f4f6fa', border: '1px solid #e8ecf4',
                borderRadius: 8, padding: '6px 12px', fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 6,
                minWidth: 200, transition: 'all 0.15s'
              }}>
                <span>🔍</span>
                <input
                  type="text"
                  placeholder="Search asset ID, hostname..."
                  value={searchVal}
                  onChange={e => setSearchVal(e.target.value)}
                  style={{
                    background: 'none', border: 'none', outline: 'none',
                    fontSize: 12, color: '#1e2a3b', width: '100%'
                  }}
                />
                {searchVal && (
                  <span
                    onClick={() => setSearchVal('')}
                    style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 14 }}>
                    ×
                  </span>
                )}
              </div>
            </form>

            {/* 3-mode theme toggle: Light → Dark → System */}
            <div
              className="topbar-icon-btn"
              onClick={cycleTheme}
              title={`Theme: ${THEME_LABELS[themeMode]} (click to cycle)`}
              style={{ position: 'relative', minWidth: 60, borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px' }}
            >
              <span style={{ fontSize: 14 }}>{THEME_ICONS[themeMode]}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>
                {THEME_LABELS[themeMode]}
              </span>
            </div>

            {/* Notifications */}
            <NotificationBell onNavigate={navigate} />

            {/* Active User Profile */}
            <UserProfile user={user} onLogout={logout} />
          </div>
        </div>

        <div className="page-content">
          {page === 'diagnostics' && <Diagnostics onNavigate={navigate} />}
          {page === 'connections' && <AgentConnections />}
          {page === 'releases'    && <PatchReleases />}
          {page === 'ai-priority' && <AiPriority />}
          {page === 'ai-chat'     && <AiChat />}
          {page === 'schedule'    && <PatchingSchedule user={user} />}
          {page === 'pvc'         && <PatchVulnCompliance user={user} />}
          {page === 'deployment'  && <PatchDeployment user={user} />}
          {page === 'devices'     && <Devices user={user} />}
          {page === 'patches'     && <Patches />}
          {page === 'compliance'  && <Compliance />}
          {page === 'validate'    && <ValidateUrls />}
          {page === 'emergency'   && <EmergencyPatches user={user} />}
          {page === 'rollback'    && <RollbackMechanism user={user} />}
          {page === 'risk'        && <RiskMatrix user={user} />}
          {page === 'bitlocker'   && <BitLockerStatus user={user} />}
          {page === 'assets'      && <AssetDiscovery />}
          {page === 'eol'         && <EolMonitor />}
          {page === 'software-library' && <SoftwareLibrary user={user} />}
          {page === 'approvals'   && <ApprovalMatrix user={user} />}
          {page === 'audit'       && role === 'admin' && <AuditLog />}
          {page === 'notifications' && role === 'admin' && <NotificationSettings />}
          {page === 'users'       && role === 'admin' && <UserManagement />}
          {page === 'certs'       && role === 'admin' && <CertificateManagement user={user} />}
        {page === 'enrollment'          && <Enrollment user={user} />}
        {page === 'remote-actions'      && <RemoteActions user={user} />}
        {page === 'file-store'          && <FileStore user={user} />}
        {page === 'profiles'            && <DeviceProfiles user={user} />}
        {page === 'device-groups'       && <DeviceGroups user={user} />}
        {page === 'jobs'                && <Jobs user={user} />}
        {page === 'reports'             && <Reports user={user} />}
        {page === 'scripting'           && <Scripting user={user} />}
        {page === 'geofencing'          && <Geofencing user={user} />}
        {page === 'mobile-mdm'          && <MobileMDM user={user} />}
        {page === 'webhooks'            && <Webhooks user={user} />}
        {page === 'conditional-access'  && <ConditionalAccess user={user} />}
        {page === 'api-keys'            && <ApiKeys user={user} />}
        {page === 'sso'                 && <SSO user={user} />}
        {page === 'push'                && <PushNotifications user={user} />}
        {page === 'scim'                && <ScimConfig user={user} />}
        {page === 'orgs'                && <Organizations user={user} />}
          {page === 'certs'       && role !== 'admin' && (
            <div className="empty" style={{ paddingTop: 80 }}>
              🔒 Admin access required to manage certificates.
            </div>
          )}
          {page === 'users'       && role !== 'admin' && (
            <div className="empty" style={{ paddingTop: 80 }}>
              🔒 Admin access required to manage users.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
